import { createContext, useContext, type ReactNode } from "react";

/**
 * Which design of the Website section is on screen (preview, 2026-10-13): the classic one at /website/…, or the
 * studio redesign at the same address with /redesign after it (/website/redesign, /website/menus/redesign,
 * /website/pages/:id/redesign). Both are the same screens with the same logic; only the layout differs, so a save or a
 * publish behaves identically in either.
 */
export type WebsiteVariant = "classic" | "studio";

const VariantContext = createContext<WebsiteVariant>("classic");

/** Wraps a route in the studio design. */
export function StudioVariant({ children }: { readonly children: ReactNode }) {
  return <VariantContext.Provider value="studio">{children}</VariantContext.Provider>;
}

export function useWebsiteVariant(): WebsiteVariant {
  return useContext(VariantContext);
}

/** An address in the design on screen: "/website/forms" stays as it is in the classic one, gains /redesign in the studio. */
export function websiteHref(variant: WebsiteVariant, path: string): string {
  if (variant === "classic") return path;
  const query = path.indexOf("?");
  const base = query === -1 ? path : path.slice(0, query);
  const rest = query === -1 ? "" : path.slice(query);
  return `${base.replace(/\/$/, "")}/redesign${rest}`;
}

/** `websiteHref` for the design on screen. */
export function useWebsiteHref(): (path: string) => string {
  const variant = useWebsiteVariant();
  return (path) => websiteHref(variant, path);
}
